import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";
import type { PMIssue } from "@/domains/pm/types";
import type { GanttScale } from "@/domains/pm/utils/ganttScale";
import { scaleWidth } from "@/domains/pm/utils/ganttScale";
import type { GanttDragMode } from "@/domains/pm/utils/ganttDrag";
import { isIssueUnscheduled } from "@/domains/pm/utils/issueDates";
import { GanttBar } from "./GanttBar";
import { IssueContextMenu } from "../IssueContextMenu";
import { GANTT_LABEL_WIDTH, GANTT_ROW_HEIGHT, ganttRowPlacement } from "./ganttConstants";
import { DraftIssueMarker, DraftIssueTitle } from "../IssueDraftViewParts";
import { useDraftIssueLabel } from "../issueDraftView";

interface GanttRowProps {
  issue: PMIssue;
  /** The band this row is in — an issue can have a row in several. */
  groupKey: string;
  /** Distance from the top of the chart body. */
  top: number;
  /** Picked up by a vertical drag: follows the pointer, above its neighbours. */
  lifted?: boolean;
  scale: GanttScale;
  /** 1 = a subissue rendered under its parent, matching the list's indent. */
  indentLevel: number;
  selected: boolean;
  onSelect: (issue: PMIssue) => void;
  /** Omitted in snapshot mode: the chart is a record, not an editor. */
  onCommit?: (issue: PMIssue, mode: GanttDragMode, dxPx: number) => void;
  /** Omitted by a read-only pane, which has no drag to report. */
  onDragActiveChange?: (active: boolean) => void;
  barLabel: (issue: PMIssue) => string;
  onLinkStart?: (issue: PMIssue, side: "start" | "end", e: React.PointerEvent) => void;
  linking?: boolean;
  /** The user moved this bar but has not confirmed it yet. */
  staged?: boolean;
  onVerticalMove?: (issue: PMIssue, clientY: number | null, groupKey: string) => void;
  onVerticalDrop?: (issue: PMIssue, clientY: number) => void;
  /** The label column's live width — the header and every row must agree. */
  labelWidth?: number;
}

export const GanttRow = observer(function GanttRow({
  issue,
  groupKey,
  top,
  lifted = false,
  scale,
  indentLevel,
  selected,
  onSelect,
  onCommit,
  onDragActiveChange,
  barLabel,
  onLinkStart,
  linking,
  staged,
  onVerticalMove,
  onVerticalDrop,
  labelWidth = GANTT_LABEL_WIDTH,
}: GanttRowProps) {
  return (
    // The same right-click menu the list rows have. ContextMenuTrigger is
    // asChild, so the row has to be a real DOM node — this div is one.
    //
    // Radix only binds pointer events for touch/pen (a 700ms long-press),
    // never mouse, so it leaves the bar's own pointer capture alone. The one
    // residual is a stationary touch-hold on a bar opening the menu mid-drag,
    // which is acceptable on a surface built for a pointer.
    <IssueContextMenu issue={issue}>
      <div
        className={cn(
          "absolute left-0 top-0 flex border-b border-border/40 motion-reduce:!transition-none",
          // Lifted, the row floats over the ones making room beneath it, so it
          // needs a surface of its own: the timeline half is otherwise clear.
          lifted && "z-30 bg-background shadow-lg ring-1 ring-border"
        )}
        style={{ height: GANTT_ROW_HEIGHT, ...ganttRowPlacement(top, lifted) }}
        data-gantt-row={issue.id}
      >
        <button
          type="button"
          // A double-click's second click would close what the first opened.
          onClick={(e) => {
            if (e.detail > 1) return;
            onSelect(issue);
          }}
          className={cn(
            // A left rule, not a fill, for BOTH hover and selection. The group
            // bands are already tinted, so a grey selected row read as another
            // band rather than as the issue the panel beside it is showing.
            //
            // The background must stay fully opaque at every state: this cell
            // is sticky over the chart, so a translucent hover fill (it was
            // `hover:bg-muted/50`) let the bars scrolled underneath show
            // straight through the issue title.
            "sticky left-0 z-10 flex shrink-0 items-center gap-1.5 border-l-2 border-r bg-background px-3 text-left text-xs",
            selected ? "border-l-[#ef4129]" : "border-l-transparent hover:border-l-[#ef4129]/40"
          )}
          style={{ width: labelWidth, paddingLeft: 10 + indentLevel * 16 }}
        >
          <span className="shrink-0 text-[10px] text-muted-foreground">#{issue.issue_number}</span>
          <span className="truncate">{issue.title}</span>
        </button>
        <div className="relative" style={{ width: scaleWidth(scale) }}>
          <GanttBar
            issue={issue}
            scale={scale}
            selected={selected}
            onSelect={onSelect}
            onCommit={onCommit}
            onDragActiveChange={onDragActiveChange}
            label={barLabel}
            onLinkStart={onLinkStart}
            linking={linking}
            staged={staged}
            onVerticalMove={
              onVerticalMove && ((moved, clientY) => onVerticalMove(moved, clientY, groupKey))
            }
            onVerticalDrop={onVerticalDrop}
          />
        </div>
      </div>
    </IssueContextMenu>
  );
});

interface GanttDraftRowProps {
  /** The draft's projection (`isDraftIssueId(issue.id)`). */
  issue: PMIssue;
  scale: GanttScale;
  /** Open in the draft panel. */
  active: boolean;
  /** Its save is running. */
  saving: boolean;
  onSelect: (draftId: number) => void;
  barLabel: (issue: PMIssue) => string;
  labelWidth?: number;
}

/**
 * A new issue still being written. Laid out in flow above the chart body
 * rather than placed in a band, so it is outside the layout every drag, drop
 * and stored band order is computed from — and a collapsed band cannot hide
 * it. Inert: no right-click menu, and its bar has no drag, resize or
 * connectors. Both the label and the bar open it in the draft panel.
 */
export const GanttDraftRow = observer(function GanttDraftRow({
  issue,
  scale,
  active,
  saving,
  onSelect,
  barLabel,
  labelWidth = GANTT_LABEL_WIDTH,
}: GanttDraftRowProps) {
  const label = useDraftIssueLabel();
  const select = () => onSelect(issue.id);
  return (
    <div
      className={cn("relative flex border-b border-border/40", saving && "opacity-70")}
      style={{ height: GANTT_ROW_HEIGHT }}
      data-gantt-draft-row={issue.id}
      aria-busy={saving || undefined}
    >
      <button
        type="button"
        aria-label={label(issue)}
        disabled={saving}
        onClick={(e) => {
          if (e.detail > 1) return;
          select();
        }}
        className={cn(
          // The issue rows' label cell: opaque, sticky, a left rule for state.
          "sticky left-0 z-10 flex shrink-0 items-center gap-1.5 border-l-2 border-r bg-background px-3 text-left text-xs",
          active ? "border-l-[#ef4129]" : "border-l-transparent hover:border-l-[#ef4129]/40"
        )}
        style={{ width: labelWidth, paddingLeft: 10 }}
      >
        <DraftIssueMarker saving={saving} />
        <DraftIssueTitle issue={issue} className="truncate" />
      </button>
      <div className="relative" style={{ width: scaleWidth(scale) }}>
        <GanttBar
          issue={issue}
          scale={scale}
          selected={active}
          onSelect={() => {
            if (!saving) select();
          }}
          label={barLabel}
        />
      </div>
    </div>
  );
});
